import {
  collection, doc, documentId, getDoc, getDocs, limit, orderBy, query, serverTimestamp,
  startAfter, updateDoc, where, type Firestore, type QueryConstraint,
} from 'firebase/firestore';
import {
  deleteObject, getBytes, ref, uploadBytes, type FirebaseStorage,
} from 'firebase/storage';
import type { FirebaseApp } from 'firebase/app';
import { connectFunctionsEmulator, getFunctions, httpsCallable, type Functions } from 'firebase/functions';
import { StoredFileSchema, type StoredFile, type StoredFileKind } from '@taki/data-model';
import type { EmulatorEndpoint } from '@taki/runtime-config';
import { timestampsToDates } from './standard-fields.js';

export const MAX_FILE_BYTES = 25 * 1024 * 1024;
export const FILES_COLLECTION = 'files';

export interface FileParent { collection: string; id: string }
export interface FileInput {
  parent: FileParent;
  name: string;
  mimeType: string;
  bytes: Blob | Uint8Array;
  /** `photo` : la photo de reference de la fiche, jamais une piece jointe (`list` ne la rend pas). */
  kind?: StoredFileKind;
}
export interface FileListOptions {
  namePrefix?: string;
  limit?: number;
  /** Dernier fichier de la page precedente, pour retrouver les homonymes. */
  after?: Pick<StoredFile, 'nameKey' | 'id'>;
}
/**
 * UN PDF A PRODUIRE : la page HTML complete (`@taki/pdf`, `documentHtml`) et la
 * fiche qui le recevra. Contrat de la callable `genererPdf` : `docs/PDF.md`.
 */
export interface PdfInput {
  parent: FileParent;
  /** Le nom affiche ; `.pdf` est ajoute s'il manque. */
  nom: string;
  html: string;
  options?: {
    format?: 'A4' | 'Letter';
    paysage?: boolean;
    /** En millimetres, de 0 a 50 chacune ; 15 par defaut. */
    marges?: Partial<Record<'haut' | 'droite' | 'bas' | 'gauche', number>>;
  };
}

/** Le refus de `genererPdf`, avec sa raison quand le serveur en donne une (`docs/PDF.md`). */
export class PdfError extends Error {
  constructor(message: string, readonly reason?: string) {
    super(message);
    this.name = 'PdfError';
  }
}

export interface FilesRepository {
  attach(input: FileInput): Promise<StoredFile>;
  /**
   * Fait convertir une page HTML en PDF par le serveur, qui le range comme
   * fichier `export` de la fiche : il apparait dans ses pieces jointes.
   */
  genererPdf(input: PdfInput): Promise<StoredFile>;
  /** Les pieces jointes de la fiche : la photo de reference (`kind: 'photo'`) n'y figure jamais, `get` la rend par son id. */
  list(parent: FileParent, options?: FileListOptions): Promise<StoredFile[]>;
  get(id: string): Promise<StoredFile | null>;
  download(id: string): Promise<Uint8Array>;
  remove(id: string): Promise<void>;
}

const SEGMENT = /^[A-Za-z0-9_-]{1,128}$/;
const MIME = /^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*$/i;
const READ_LIMIT = 50;
const functionEmulators = new WeakSet<Functions>();

function withTimeout<T>(operation: Promise<T>, milliseconds: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), milliseconds);
    operation.then(
      value => { clearTimeout(timer); resolve(value); },
      error => { clearTimeout(timer); reject(error); },
    );
  });
}

function parseFile(raw: unknown, id: string): StoredFile {
  return StoredFileSchema.parse({ ...(timestampsToDates(raw) as object), id });
}

export function fileStoragePath(orgId: string, parent: FileParent, fileId: string): string {
  if (![orgId, parent.collection, parent.id, fileId].every(value => SEGMENT.test(value))) {
    throw new Error('Chemin de fichier invalide.');
  }
  return `orgs/${orgId}/records/${parent.collection}/${parent.id}/files/${fileId}`;
}

/**
 * Fichiers d'une fiche. Une lecture du binaire passe toujours par getBytes,
 * jamais par une URL de telechargement durable. Le chemin est opaque pour l'app.
 */
export function createFilesRepository(
  db: Firestore,
  storage: FirebaseStorage,
  getActorId: () => string,
  orgId: string,
  localStorageConfigured: boolean,
  app?: FirebaseApp,
  functionsEmulator?: EmulatorEndpoint,
  localFunctionsConfigured = true,
): FilesRepository {
  const col = collection(db, FILES_COLLECTION);
  function requireStorage(): void {
    if (!localStorageConfigured) throw new Error('Emulateur Storage absent de la configuration locale.');
  }
  function callable<Requete, Reponse>(name: string) {
    if (!app || !localFunctionsConfigured) throw new Error('Emulateur Functions absent de la configuration locale.');
    const functions = getFunctions(app, 'europe-west1');
    if (functionsEmulator && !functionEmulators.has(functions)) {
      connectFunctionsEmulator(functions, functionsEmulator.host, functionsEmulator.port);
      functionEmulators.add(functions);
    }
    return httpsCallable<Requete, Reponse>(functions, name);
  }
  return {
    async attach(input): Promise<StoredFile> {
      requireStorage();
      const name = input.name.trim();
      const mimeType = input.mimeType.trim().toLowerCase();
      if (!name || name.length > 255 || !MIME.test(mimeType)) throw new Error('Nom ou type de fichier invalide.');
      const size = input.bytes instanceof Blob ? input.bytes.size : input.bytes.byteLength;
      if (size === undefined || size <= 0 || size > MAX_FILE_BYTES) throw new Error('Taille de fichier invalide.');
      fileStoragePath(orgId, input.parent, 'probe');
      /*
       * LE TICKET D'ABORD. Le serveur lit la declaration du jeu (fichiers
       * ouverts, types, taille) et les droits de la personne, puis pose la
       * metadonnee `pending` ; les regles Storage n'acceptent que l'envoi qui
       * lui correspond. Aucune creation de `files` ne part du navigateur.
       */
      const ticket = await callable<
        { parent: FileParent; name: string; mimeType: string; size: number; kind: string },
        { fileId: string; storagePath: string }
      >('preparerFichier')({ parent: input.parent, name, mimeType, size, kind: input.kind ?? 'attachment' });
      const { fileId, storagePath } = ticket.data;
      if (!SEGMENT.test(fileId) || storagePath !== fileStoragePath(orgId, input.parent, fileId)) {
        throw new Error('Reponse de preparation de fichier invalide.');
      }
      const fileRef = doc(col, fileId);
      await uploadBytes(ref(storage, storagePath), input.bytes, { contentType: mimeType });
      await updateDoc(fileRef, { status: 'ready', updatedAt: serverTimestamp(), updatedBy: getActorId() });
      const snap = await getDoc(fileRef);
      return parseFile(snap.data(), snap.id);
    },
    async genererPdf(input): Promise<StoredFile> {
      fileStoragePath(orgId, input.parent, 'probe');
      let reponse;
      try {
        reponse = await callable<PdfInput, { fileId: string; storagePath: string }>('genererPdf')(input);
      } catch (error) {
        const { message, details } = (error ?? {}) as { message?: unknown; details?: { reason?: unknown } };
        throw new PdfError(
          typeof message === 'string' && message !== '' ? message : 'Le PDF n’a pas pu être produit.',
          typeof details?.reason === 'string' ? details.reason : undefined,
        );
      }
      const { fileId, storagePath } = reponse.data;
      if (!SEGMENT.test(fileId) || storagePath !== fileStoragePath(orgId, input.parent, fileId)) {
        throw new PdfError('Réponse du moteur PDF invalide.');
      }
      const snap = await getDoc(doc(col, fileId));
      return parseFile(snap.data(), snap.id);
    },
    async list(parent, options = {}): Promise<StoredFile[]> {
      fileStoragePath(orgId, parent, 'probe');
      const count = Math.min(Math.max(options.limit ?? READ_LIMIT, 1), READ_LIMIT);
      const constraints: QueryConstraint[] = [
        where('orgId', '==', orgId), where('parentCollection', '==', parent.collection),
        where('parentId', '==', parent.id), where('status', '==', 'ready'),
        where('archivedAt', '==', null), where('deletedAt', '==', null),
      ];
      if (options.namePrefix) {
        const prefix = options.namePrefix.toLocaleLowerCase('fr').normalize('NFKD');
        constraints.push(where('nameKey', '>=', prefix), where('nameKey', '<=', `${prefix}\uf8ff`));
      }
      constraints.push(orderBy('nameKey'), orderBy(documentId()));
      if (options.after) constraints.push(startAfter(options.after.nameKey, options.after.id));
      // Une photo n'est pas une piece jointe. Sans index de plus : un document de
      // plus est demande, la photo (une seule vivante par fiche) est ecartee, la page reste pleine.
      constraints.push(limit(count + 1));
      const snap = await getDocs(query(col, ...constraints));
      return snap.docs.map(item => parseFile(item.data(), item.id)).filter(file => file.kind !== 'photo').slice(0, count);
    },
    async get(id): Promise<StoredFile | null> {
      if (!SEGMENT.test(id)) throw new Error('Identifiant de fichier invalide.');
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) return null;
      const file = parseFile(snap.data(), snap.id);
      return file.status === 'ready' && file.archivedAt === null && file.deletedAt === null ? file : null;
    },
    async download(id): Promise<Uint8Array> {
      requireStorage();
      const file = await this.get(id);
      if (!file) throw new Error('Fichier introuvable.');
      const bytes = await withTimeout(
        getBytes(ref(storage, file.storagePath), MAX_FILE_BYTES),
        15000,
        'Téléchargement interrompu. Rétablis la connexion puis réessaie.',
      );
      return new Uint8Array(bytes);
    },
    async remove(id): Promise<void> {
      requireStorage();
      const file = await this.get(id);
      if (!file) throw new Error('Fichier introuvable.');
      try {
        await deleteObject(ref(storage, file.storagePath));
      } catch (error) {
        // Une coupure apres la suppression du binaire laisse la metadata ready.
        // Le second essai termine la suppression sans exiger que l'objet existe.
        if ((error as { code?: unknown } | null)?.code !== 'storage/object-not-found') throw error;
      }
      await updateDoc(doc(col, id), {
        archivedAt: serverTimestamp(), deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(), updatedBy: getActorId(),
      });
    },
  };
}
